import { useContext, useMemo } from "react";
import { useHistory } from "react-router";

import { UserContext } from "@/components/User";
import { IConsultantProfile, postConsultantProfile } from "@/data/consultant-profiles";
import { refresh } from "@/data/session";
import { putUser } from "@/data/users";
import { SessionContext } from "@/infrastructure/Session";
import { router } from "@/views/router";

export const useOnboardingActions = () => {
  const User = useContext(UserContext);
  const Session = useContext(SessionContext);
  const history = useHistory();

  const defaultConsultantData: Partial<IConsultantProfile> = useMemo(
    () => ({
      firstName: User.sessionUser.firstName,
      lastName: User.sessionUser.lastName,
      email: User.sessionUser.email,
    }),
    [User.sessionUser],
  );

  const redirectJobRequestId = useMemo(() => {
    return (
      (!User.sessionUser.onboardingDisplayed || !User.contextConsultantProfileId) &&
      User.sessionUser.onboardingJobRequestId
    );
  }, [User]);

  const finishOnboarding = async (profileId?: number, cb?: () => void) => {
    await putUser(
      { userId: User.sessionUser.id },
      { ...User.sessionUser, onboardingDisplayed: true, lastUsedConsultantProfileId: profileId ?? null },
    );

    const sessionUser = await refresh();
    Session.update(sessionUser, () => {
      if (typeof profileId === "number") {
        User.setConsultantProfileContext(profileId);
      }

      cb?.();
    });

    if (redirectJobRequestId) {
      history.push(router.get("job-requests.detail", { jobRequestId: redirectJobRequestId }).path);
    }
  };

  const privateProfileQuickSetup = (cb?: () => void) => async () => {
    const newProfile = await postConsultantProfile({}, defaultConsultantData);

    await finishOnboarding(newProfile.id, () => {
      if (redirectJobRequestId) {
        history.push(router.get("job-requests.detail", { jobRequestId: redirectJobRequestId }).path);
      } else {
        history.push(router.get("dashboard").path);
      }

      cb?.();
    });
  };

  return {
    redirectJobRequestId,
    finishOnboarding,
    privateProfileQuickSetup,
    defaultConsultantData,
  };
};
